import { green, grey, red } from "@mui/material/colors";
import {
    Box, Button, CircularProgress, Chip,
    Divider, IconButton, InputAdornment,
    MenuItem, TextField, Typography
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useEffect, useState } from "react";
import { Search } from "@mui/icons-material";

export function TutorialLockerThirdStepPanel() {
    return (
        <Box sx={{ border: `1px solid ${grey[200]}`, borderRadius: 2, p: 2, backgroundColor: grey[50] }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <InfoOutlinedIcon sx={{ color: '#1E5139', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600}>Instruções de Designação:</Typography>
            </Box>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {[
                    'Clique em uma porta para designar um produto.',
                    'Busque e selecione o produto na lista.',
                    'Se houver variações, escolha uma.',
                    'Defina a quantidade disponível na porta.',
                    'Para trocar o produto, remova o atual primeiro.',
                ].map((txt, i) => (
                    <Box component="li" key={i} sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">{txt}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ProdutoCard({ prod, selected, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                p: 1.5, borderRadius: 1.5, cursor: 'pointer',
                border: `1.5px solid ${selected ? '#1E5139' : grey[200]}`,
                backgroundColor: selected ? grey[100] : 'white',
                '&:hover': { borderColor: '#1E5139', backgroundColor: grey[100] },
                transition: 'all .15s',
            }}
        >
            {prod.codigos?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5, mb: .75 }}>
                    {prod.codigos.map((cod, i) => (
                        <Chip
                            key={i}
                            label={cod}
                            size="small"
                            sx={{
                                fontSize: 10, height: 20,
                                backgroundColor: selected ? green[100] : grey[100],
                                color: selected ? '#1E5139' : grey[700],
                                border: `1px solid ${selected ? '#1E5139' : grey[300]}`,
                            }}
                        />
                    ))}
                </Box>
            )}

            <Typography
                variant="body2"
                fontWeight={selected ? 700 : 500}
                sx={{ lineHeight: 1.3, mb: .25, color: selected ? '#1E5139' : 'text.primary' }}
            >
                {prod.nome}
            </Typography>

            {(prod?.categoria?.nome || typeof prod?.categoria === 'string') && (
                <Typography variant="caption" color="text.secondary">
                    {prod?.categoria?.nome ?? prod?.categoria}
                </Typography>
            )}

            {prod.filhos?.length > 0 && (
                <Box sx={{ mt: .5 }}>
                    <Chip
                        label={`${prod.filhos.length} variação(ões)`}
                        size="small"
                        sx={{
                            fontSize: 10, height: 18,
                            backgroundColor: selected ? green[100] : grey[100],
                            color: selected ? '#1E5139' : grey[600],
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}

function ProdutoAtribuidoCard({ prod, variacao, quantidade, onRemove, readOnly = false }) {
    const nomeExibido = variacao?.nome ? `${prod.nome} — ${variacao.nome}` : prod.nome;

    return (
        <Box sx={{
            p: 1.5, borderRadius: 1.5,
            border: `1.5px solid ${green[400]}`,
            backgroundColor: green[50],
            display: 'flex', flexDirection: 'column', gap: 1,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                    <CheckCircleOutlineIcon sx={{ color: green[600], fontSize: 18, flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#1E5139', lineHeight: 1.3 }}>
                        {nomeExibido}
                    </Typography>
                </Box>
            </Box>

            {prod.codigos?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
                    {prod.codigos.map((cod, i) => (
                        <Chip
                            key={i}
                            label={cod}
                            size="small"
                            sx={{
                                fontSize: 10, height: 20,
                                backgroundColor: green[100],
                                color: '#1E5139',
                                border: `1px solid ${green[300]}`,
                            }}
                        />
                    ))}
                </Box>
            )}

            {(prod?.categoria?.nome || typeof prod?.categoria === 'string') && (
                <Typography variant="caption" color="text.secondary">
                    {prod?.categoria?.nome ?? prod?.categoria}
                </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: .5 }}>
                <Typography variant="caption" sx={{ color: green[700], fontWeight: 600 }}>
                    Qtd: {quantidade}
                </Typography>

                {!readOnly && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={onRemove}
                        sx={{ py: .25, px: 1, fontSize: 11 }}
                    >
                        Remover produto
                    </Button>
                )}
            </Box>
        </Box>
    );
}

export function SetProductThirdStep({ porta, produtos, loadingProdutos, onConfirm, onRemove, onClose, readOnly = false }) {
    const [search, setSearch] = useState('');
    const [produtoSelecionado, setProd] = useState(null);
    const [variacao, setVariacao]  = useState('');
    const [quantidade, setQtd] = useState(1);

    const produtoAtribuido = porta.id_produto
        ? produtos.find(p => p.id === (porta.id_produto_pai || porta.id_produto)) ?? null
        : null;

    const variacaoAtribuida = produtoAtribuido && porta.id_produto_pai
        ? produtoAtribuido.filhos?.find(v => v.id === porta.id_produto) ?? null
        : null;

    const temProdutoAtribuido = !!produtoAtribuido;

    useEffect(() => {
        setProd(null);
        setVariacao('');
        setSearch('');
        setQtd(porta.qtd_produto || 1);
    }, [porta.id, produtos]);

    const filtered = produtos.filter(p =>
        p.nome.toLowerCase().includes(search.toLowerCase()) ||
        p.codigos?.some(c => c.toLowerCase().includes(search.toLowerCase())) ||
        p.filhos?.some(v => v.nome.toLowerCase().includes(search.toLowerCase()))
    );

    const temVariacoes = produtoSelecionado?.filhos?.length > 0;

    const filhoInativo = (filho) =>
        filho?.status === 0 || filho?.status === '0' || filho?.status === false || filho?.ativo === false;

    const variacaoEscolhida = produtoSelecionado?.filhos?.find(v => v.id === variacao);
    const variacaoInativaEscolhida = temVariacoes && variacao && filhoInativo(variacaoEscolhida);

    const handleSelectProduto = (prod) => {
        setProd(prod);
        setVariacao('');
        if (prod.filhos?.length === 1 && !filhoInativo(prod.filhos[0])) {
            setVariacao(prod.filhos[0].id);
        }
    };

    const podeSalvar = produtoSelecionado
        && (!temVariacoes || (variacao && !variacaoInativaEscolhida))
        && quantidade >= 1;

    const handleConfirm = () => {
        onConfirm({
            id_produto:     temVariacoes ? variacao : produtoSelecionado.id,
            id_produto_pai: temVariacoes ? produtoSelecionado.id : null,
            qtd_produto:    quantidade,
            mustSelectChild: false,
        });
    };

    return (
        <Box sx={{
            border: `1px solid #1E4139`,
            borderRadius: 2, p: 2,
            display: 'flex', flexDirection: 'column', gap: 1.5,
            maxHeight: 570, overflow: 'hidden',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1E4139' }}>
                    Porta {porta.numeroLogico ?? porta.numero_logico ?? '—'}
                    {temProdutoAtribuido && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: green[700] }}>
                            ● com produto
                        </Typography>
                    )}
                </Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Divider sx={{ borderColor: '#1E4139' }} />

            {loadingProdutos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#1E5139' }} />
                </Box>
            ) : temProdutoAtribuido ? (
                <ProdutoAtribuidoCard
                    prod={produtoAtribuido}
                    variacao={variacaoAtribuida}
                    quantidade={porta.qtd_produto}
                    onRemove={onRemove}
                    readOnly={readOnly}
                />
            ) : (
                <>
                    <TextField
                        size="small"
                        placeholder="Buscar produto ou código..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setProd(null); setVariacao(''); }}
                        disabled={readOnly}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': { borderColor: '#1E4139' },
                            },
                        }}
                        fullWidth
                    />

                    <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: .75, pr: .5 }}>
                        {filtered.length === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                                Nenhum produto encontrado.
                            </Typography>
                        )}
                        {filtered.map(prod => (
                            <ProdutoCard
                                key={prod.id}
                                prod={prod}
                                selected={produtoSelecionado?.id === prod.id}
                                onClick={() => { if (!readOnly) handleSelectProduto(prod); }}
                            />
                        ))}
                    </Box>

                    {temVariacoes && (
                        <TextField
                            select
                            size="small"
                            label="Variação"
                            value={variacao}
                            onChange={e => setVariacao(e.target.value)}
                            disabled={readOnly}
                            fullWidth
                            sx={{
                                backgroundColor: 'white', borderRadius: 1,
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#1E5139' },
                                '& label.Mui-focused': { color: '#1E5139' },
                            }}
                        >
                            {produtoSelecionado.filhos.map(v => {
                                const inativo = filhoInativo(v);
                                return (
                                    <MenuItem
                                        key={v.id}
                                        value={v.id}
                                        disabled={inativo}
                                        sx={inativo ? { opacity: 0.5 } : undefined}
                                    >
                                        {v.nome}{inativo ? ' (inativo)' : ''}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    )}

                    {temVariacoes && produtoSelecionado.filhos.every(filhoInativo) && (
                        <Typography variant="caption" color="error">
                            Todas as variações deste produto estão inativas — não é possível designá-lo a esta porta.
                        </Typography>
                    )}

                    {produtoSelecionado && (
                        <TextField
                            size="small"
                            label="Quantidade"
                            type="number"
                            value={quantidade}
                            onChange={e => setQtd(Math.max(1, parseInt(e.target.value) || 1))}
                            disabled
                            inputProps={{ min: 1, max: 1 }}
                            fullWidth
                            sx={{
                                backgroundColor: 'white', borderRadius: 1,
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#1E5139' },
                                '& label.Mui-focused': { color: '#1E5139' },
                                mt: 1,
                            }}
                        />
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        fullWidth
                        disabled={!podeSalvar || readOnly}
                        onClick={handleConfirm}
                    >
                        Confirmar
                    </Button>
                </>
            )}
        </Box>
    );
}